import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Gift picker component for cart drawer.
 * Handles single-pick: only one gift can be selected at a time.
 * Uses event delegation so click handling survives DOM morphs.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} carousel - The scrollable carousel container.
 *
 * @extends {Component<Refs>}
 */
class GiftPickerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * Gets section IDs for cart re-rendering.
   * @returns {string}
   */
  #getSectionIds() {
    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sections.add(el.dataset.sectionId);
      }
    });
    return Array.from(sections).join(',');
  }

  /**
   * Finds the currently selected gift variant ID from the component
   * data attribute or the DOM as a fallback.
   * @returns {string}
   */
  #getSelectedVariant() {
    const fromAttr = this.dataset.selectedVariant;
    if (fromAttr) return fromAttr;

    const selectedCard = this.querySelector('.offer-gift-card--selected');
    return selectedCard?.dataset?.variantId || '';
  }

  /**
   * Delegated click handler. Finds the closest gift card from the
   * click target and processes the selection.
   * @param {Event} event
   */
  #handleClick = async (event) => {
    const card = /** @type {HTMLElement} */ (event.target)?.closest('.offer-gift-card');
    if (!card) return;
    const variantId = card.dataset.variantId;
    const eligible = card.dataset.eligible === 'true';
    if (!variantId || !eligible) return;

    const selectedVariant = this.#getSelectedVariant();
    if (selectedVariant === variantId) return;

    card.style.opacity = '0.5';
    card.style.pointerEvents = 'none';

    try {
      if (selectedVariant) {
        await this.#removeCurrentGift(selectedVariant);
      }

      const sectionIds = this.#getSectionIds();
      const body = {
        items: [{
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: { _gift: 'true' },
        }],
      };

      if (sectionIds) {
        body.sections = sectionIds;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();

        this.dataset.selectedVariant = variantId;

        document.dispatchEvent(
          new CartAddEvent(data, 'gift-picker', {
            source: 'gift-picker-component',
            itemCount: 1,
            variantId: String(variantId),
            sections: data.sections || {},
          })
        );
      } else {
        card.style.opacity = '';
        card.style.pointerEvents = '';
      }
    } catch (error) {
      console.error('Failed to add gift:', error);
      card.style.opacity = '';
      card.style.pointerEvents = '';
    }
  };

  /**
   * Removes the currently selected gift from cart before adding a new one.
   * @param {string} variantId - The variant ID of the gift to remove.
   */
  async #removeCurrentGift(variantId) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const lineIndex = cart.items.findIndex(
        (item) => String(item.variant_id) === String(variantId)
          && item.properties?._gift === 'true'
      );

      if (lineIndex === -1) return;

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex + 1,
          quantity: 0,
        }),
      });
    } catch {
      // Silently continue — the new gift will be added regardless
    }
  }
}

if (!customElements.get('gift-picker-component')) {
  customElements.define('gift-picker-component', GiftPickerComponent);
}

/**
 * Document-level fallback click handler for gift cards.
 * Catches clicks that happen before the component's connectedCallback
 * runs (e.g. after a morph recreates the element while this module
 * is still loading). Only fires when the component hasn't handled
 * the event itself.
 */
if (!window.__giftPickerFallbackAttached) {
  window.__giftPickerFallbackAttached = true;
  document.addEventListener('click', (event) => {
    const card = /** @type {HTMLElement} */ (event.target)?.closest('.offer-gift-card');
    if (!card) return;

    // If the component is already upgraded, let it handle the event
    const picker = card.closest('gift-picker-component');
    if (picker && typeof picker.connectedCallback === 'function' && picker.isConnected) return;

    // Fallback: handle the click directly
    const variantId = card.dataset.variantId;
    const eligible = card.dataset.eligible === 'true';
    if (!variantId || !eligible) return;

    // Prevent duplicate handling
    if (card.dataset.processing === 'true') return;
    card.dataset.processing = 'true';

    const selectedCard = picker?.querySelector('.offer-gift-card--selected');
    const selectedVariant = picker?.dataset?.selectedVariant
      || selectedCard?.dataset?.variantId || '';

    if (selectedVariant === variantId) {
      delete card.dataset.processing;
      return;
    }

    card.style.opacity = '0.5';
    card.style.pointerEvents = 'none';

    (async () => {
      try {
        // Remove current gift if any
        if (selectedVariant) {
          const cartRes = await fetch('/cart.js');
          const cartData = await cartRes.json();
          const lineIndex = cartData.items.findIndex(
            (item) => String(item.variant_id) === String(selectedVariant)
              && item.properties?._gift === 'true'
          );
          if (lineIndex !== -1) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ line: lineIndex + 1, quantity: 0 }),
            });
          }
        }

        // Get section IDs
        const sections = new Set();
        document.querySelectorAll('cart-items-component').forEach((el) => {
          if (el instanceof HTMLElement && el.dataset.sectionId) {
            sections.add(el.dataset.sectionId);
          }
        });
        const sectionIds = Array.from(sections).join(',');

        const body = {
          items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { _gift: 'true' } }],
        };
        if (sectionIds) {
          body.sections = sectionIds;
          body.sections_url = window.location.pathname;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          const data = await response.json();
          if (picker) picker.dataset.selectedVariant = variantId;
          document.dispatchEvent(
            new CartAddEvent(data, 'gift-picker', {
              source: 'gift-picker-component',
              itemCount: 1,
              variantId: String(variantId),
              sections: data.sections || {},
            })
          );
        } else {
          card.style.opacity = '';
          card.style.pointerEvents = '';
        }
      } catch {
        card.style.opacity = '';
        card.style.pointerEvents = '';
      } finally {
        delete card.dataset.processing;
      }
    })();
  });
}
